// One level of the depth reader as a scrolling page (ios LevelView.swift): Outline, Gist, Original, Explained.
// Author text is serif; AI text is sans with a verdigris label. Paragraph text is a single text node, so character
// offsets (UTF-16, as everywhere in Depth) map straight onto DOM ranges.
import { kicker, type DepthDocument } from "../levels/document";
import { toHTML } from "../levels/markup";
import { anchorKeyFor, beatIndex, Level, sectionIndex, sectionParagraphs, type AnchorKey, type Structure } from "../levels/types";
import type { ArticleImage } from "../model/images";
import { h, plural } from "../ui/dom";

export type NoteDisplay = { kind: "idle" | "loading" } | { kind: "streaming" | "text"; text: string } | { kind: "failed"; message: string; needsKey: boolean };
export type StructureDisplay = { kind: "ready" | "loading" } | { kind: "failed"; message: string; needsKey: boolean };

export interface LevelContext {
  doc: DepthDocument;
  chapter: number;
  structure: Structure | null;
  structureDisplay: StructureDisplay;
  readingParagraph: number;
  note: (beat: number) => NoteDisplay;
  onTapAnchor: (key: AnchorKey) => void;
  onRetryNote: (beat: number) => void;
  onRetryStructure: () => void;
  onSignIn: () => void;
  onImage: (img: ArticleImage) => void;
}

export const keyId = (k: AnchorKey) => `${k.kind}:${k.index}`;

const supportsHighlights = typeof CSS !== "undefined" && "highlights" in CSS && typeof (window as any).Highlight === "function";

export class LevelView {
  readonly el: HTMLDivElement;
  readonly column: HTMLDivElement;
  readonly level: Level;
  ctx: LevelContext;
  anchors: { key: AnchorKey; el: HTMLElement }[] = [];
  private byKey = new Map<string, HTMLElement>();
  paragraphEls = new Map<number, HTMLElement>();
  noteEls = new Map<number, HTMLElement>();
  private overlay: HTMLDivElement;
  private builtWithStructure = false;
  private narrationKey: string | null = null;
  private narratingAnchor: HTMLElement | null = null;

  constructor(level: Level, ctx: LevelContext) {
    this.level = level;
    this.ctx = ctx;
    this.column = h("div.column");
    this.overlay = h("div.hl-overlay");
    this.el = h("div.level", { dataset: { level: String(level) }, tabindex: "-1" }, h("div.column-wrap", null, this.overlay, this.column));
    this.build();
  }

  get structure() { return this.ctx.structure; }

  // ---- building

  private build() {
    this.column.textContent = "";
    this.anchors = []; this.byKey.clear(); this.paragraphEls.clear(); this.noteEls.clear();
    this.narrationKey = null; this.narratingAnchor = null;
    this.builtWithStructure = !!this.ctx.structure;
    if (this.level >= 0) this.buildText();
    else if (this.level === Level.Gist) this.buildGist();
    else this.buildOutline();
  }

  update(ctx: LevelContext) {
    const rebuild = !!ctx.structure !== this.builtWithStructure || ctx.structure !== this.ctx.structure
      || (this.level < 0 && ctx.readingParagraph !== this.ctx.readingParagraph) || ctx.structureDisplay.kind !== this.ctx.structureDisplay.kind;
    this.ctx = ctx;
    if (rebuild) this.preservingPosition(() => this.build());
    else this.refreshNotes();
  }

  /** Keeps the anchor at the top of the screen where it was across a rebuild. */
  preservingPosition(change: () => void) {
    const a = this.anchorAt(this.el.clientHeight * 0.25);
    const before = a ? this.frame(a.el).top : 0;
    const key = a?.key;
    change();
    if (key) {
      const el = this.anchor(key) ?? this.anchor(anchorKeyFor(this.structure, (this.paragraphsOf(key) ?? [0])[0], this.level) ?? key);
      if (el) this.el.scrollTop += this.frame(el).top - before;
    }
  }

  private add(key: AnchorKey, el: HTMLElement) {
    el.classList.add("anchor");
    el.dataset.key = keyId(key);
    this.anchors.push({ key, el });
    this.byKey.set(keyId(key), el);
    this.column.appendChild(el);
  }

  private eyebrow(text: string) {
    this.column.appendChild(h("div.eyebrow", null, h("span.ring"), text.toUpperCase()));
  }

  private header(small: boolean) {
    const ch = this.ctx.doc.chapters[this.ctx.chapter];
    this.column.appendChild(h("div.kicker", null, kicker(this.ctx.doc).toUpperCase()));
    this.column.appendChild(h(small ? "h2.title.small" : "h1.title", null, ch.title));
    if (!small && this.ctx.doc.dek && !this.ctx.doc.isBook) this.column.appendChild(h("p.dek", null, this.ctx.doc.dek));
  }

  private status(display: StructureDisplay, loadingText: string): HTMLElement {
    if (display.kind === "failed") {
      const msg = display.needsKey ? (display.message || "Sign in to use this.") : display.message;
      return h("div.status.failed", null, h("span", null, msg),
        h("button.link", { onclick: () => (display.needsKey ? this.ctx.onSignIn() : this.ctx.onRetryStructure()) }, display.needsKey ? "Sign in" : "Retry"));
    }
    return h("div.status", null, h("span.pulse"), h("span", null, loadingText));
  }

  private buildText() {
    const ch = this.ctx.doc.chapters[this.ctx.chapter];
    const explained = this.level === Level.Explained;
    if (explained) this.eyebrow("Explained · AI notes between the author’s paragraphs");
    this.header(false);
    if (explained && !this.structure) this.column.appendChild(this.status(this.ctx.structureDisplay.kind === "ready" ? { kind: "loading" } : this.ctx.structureDisplay, "Preparing notes for this chapter…"));
    const lastOfBeat = new Map<number, number>();
    this.structure?.beats.forEach((b, i) => lastOfBeat.set(b.to, i));
    const imagesBefore = new Map<number, ArticleImage[]>();
    for (const img of ch.images) {
      const k = Math.min(img.before, ch.paragraphs.length);
      imagesBefore.set(k, [...(imagesBefore.get(k) ?? []), img]);
    }
    ch.paragraphs.forEach((p, i) => {
      for (const img of imagesBefore.get(i) ?? []) this.column.appendChild(this.image(img));
      const el = h("p.para", { dataset: { p: String(i) } }, p.text);
      if (p.kind === "quote") el.classList.add("quote");
      if (p.kind === "heading") el.classList.add("heading");
      if (p.dimmed) { el.classList.add("dimmed"); el.title = "Skipped: " + (p.skipReason ?? "not narrated"); }
      this.paragraphEls.set(i, el);
      this.add({ kind: "paragraph", index: i }, el);
      const beat = lastOfBeat.get(i);
      if (explained && beat !== undefined) {
        const note = h("aside.note", { dataset: { beat: String(beat) } });
        this.noteEls.set(beat, note);
        this.renderNote(note, beat);
        this.column.appendChild(note);
      }
    });
    for (const img of imagesBefore.get(ch.paragraphs.length) ?? []) this.column.appendChild(this.image(img));
    this.footer();
  }

  private image(img: ArticleImage): HTMLElement {
    const ratio = img.width > 0 && img.height > 0 ? `${img.width} / ${img.height}` : "3 / 2";
    const im = h("img", { src: img.src, alt: img.alt || img.caption || "Image", loading: "lazy", referrerpolicy: "no-referrer-when-downgrade", decoding: "async" });
    im.addEventListener("load", () => { fig.style.aspectRatio = ""; fig.classList.add("loaded"); }, { once: true });
    im.addEventListener("error", () => fig.classList.add("broken"), { once: true });
    const fig = h("figure.figure", { style: { aspectRatio: ratio }, onclick: () => this.ctx.onImage(img) }, im);
    const cap = img.caption || img.alt;
    return h("div.figure-wrap", null, fig, cap ? h("figcaption", null, cap) : null);
  }

  renderNote(el: HTMLElement, beat: number) {
    const d = this.ctx.note(beat);
    const sig = JSON.stringify(d);
    if (el.dataset.sig === sig) return;
    el.dataset.sig = sig;
    el.textContent = "";
    el.appendChild(h("div.note-label", null, "CONTEXT"));
    if (d.kind === "text" || d.kind === "streaming") el.appendChild(h("div.note-text", { html: toHTML(d.text) }));
    else if (d.kind === "failed") {
      el.appendChild(h("div.note-text.failed", null, d.message, " ",
        h("button.link", { onclick: (e: Event) => { e.stopPropagation(); d.needsKey ? this.ctx.onSignIn() : this.ctx.onRetryNote(beat); } }, d.needsKey ? "Sign in" : "Retry")));
    } else el.appendChild(h("div.skeleton", null, h("i"), h("i"), h("i.short")));
    el.classList.toggle("streaming", d.kind === "streaming");
  }

  refreshNotes() { for (const [beat, el] of this.noteEls) this.renderNote(el, beat); }

  private footer() {
    this.column.appendChild(h("hr.rule"));
    this.column.appendChild(h("div.kicker.end", null, this.ctx.doc.isBook ? "The end" : "End of article"));
  }

  private placeholder() {
    this.eyebrow(this.level === Level.Outline ? "Outline" : "Gist");
    this.header(this.level === Level.Gist);
    this.column.appendChild(this.status(this.ctx.structureDisplay.kind === "ready" ? { kind: "loading" } : this.ctx.structureDisplay, "Reading this chapter…"));
    const sk = h("div.skeleton.big");
    for (let i = 0; i < (this.level === Level.Outline ? 5 : 8); i++) sk.appendChild(h("i"));
    this.column.appendChild(sk);
  }

  private buildGist() {
    const s = this.structure;
    if (!s) return this.placeholder();
    const count = this.ctx.doc.chapters[this.ctx.chapter].paragraphs.length;
    this.eyebrow(`Gist · ${plural(s.beats.length, "line")} from ${plural(count, "paragraph")}`);
    this.header(true);
    const here = beatIndex(s, this.ctx.readingParagraph);
    s.sections.forEach((sec) => {
      this.column.appendChild(h("div.gist-section", null, sec.label.toUpperCase()));
      for (let b = sec.firstBeat; b <= sec.lastBeat; b++) {
        const beat = s.beats[b];
        if (!beat) continue;
        const row = h("div.gist-row", { role: "button", tabindex: "0", "aria-label": beat.gist },
          h("span.count", { class: b === here ? "count here" : "count" }, `${beat.to - beat.from + 1}¶`), h("span.gist", null, beat.gist));
        row.addEventListener("click", (e) => { e.stopPropagation(); this.ctx.onTapAnchor({ kind: "beat", index: b }); });
        row.addEventListener("keydown", (e) => { if (e.key === "Enter") this.ctx.onTapAnchor({ kind: "beat", index: b }); });
        this.add({ kind: "beat", index: b }, row);
      }
    });
    this.column.appendChild(h("div.spacer"));
  }

  private buildOutline() {
    const s = this.structure;
    if (!s) return this.placeholder();
    const count = this.ctx.doc.chapters[this.ctx.chapter].paragraphs.length;
    this.eyebrow(`Outline · AI summary of ${plural(count, "paragraph")}`);
    this.header(false);
    if (s.summary) this.column.appendChild(h("p.lead", null, s.summary));
    const hb = beatIndex(s, this.ctx.readingParagraph);
    const here = hb === null ? null : sectionIndex(s, hb);
    s.sections.forEach((sec, i) => {
      const [a, b] = sectionParagraphs(s, i);
      const item = h("div.outline-item", { role: "button", tabindex: "0" },
        h("span.num", null, String(i + 1)),
        h("div.outline-body", null,
          h("div.outline-label", null, sec.label.toUpperCase(), i === here ? h("span.here", null, "YOU’RE HERE") : null),
          h("div.outline-summary", null, sec.summary),
          h("div.meta", null, a === b ? `¶ ${a + 1}` : `¶ ${a + 1}–${b + 1}`)));
      item.addEventListener("click", (e) => { e.stopPropagation(); this.ctx.onTapAnchor({ kind: "section", index: i }); });
      item.addEventListener("keydown", (e) => { if (e.key === "Enter") this.ctx.onTapAnchor({ kind: "section", index: i }); });
      if (i === here) item.classList.add("is-here");
      this.add({ kind: "section", index: i }, item);
    });
    this.column.appendChild(h("hr.rule"));
  }

  // ---- geometry (all y in the level's own visible coordinates: 0 = top of the page area)

  frame(el: Element): DOMRect {
    const r = el.getBoundingClientRect(), o = this.el.getBoundingClientRect();
    return new DOMRect(r.left - o.left, r.top - o.top, r.width, r.height);
  }

  anchor(key: AnchorKey): HTMLElement | undefined { return this.byKey.get(keyId(key)); }

  paragraphsOf(key: AnchorKey): [number, number] | null {
    if (key.kind === "paragraph") return [key.index, key.index];
    const s = this.structure;
    if (!s) return null;
    if (key.kind === "beat") return s.beats[key.index] ? [s.beats[key.index].from, s.beats[key.index].to] : null;
    return s.sections[key.index] ? sectionParagraphs(s, key.index) : null;
  }

  /** The anchor at a visible y and how far down it the point is. Images and notes aren't anchors: nearest wins. */
  anchorAt(y: number): { key: AnchorKey; el: HTMLElement; frac: number } | null {
    let best: { key: AnchorKey; el: HTMLElement; frac: number } | null = null, bestD = Infinity;
    for (const a of this.anchors) {
      const f = this.frame(a.el);
      if (y >= f.top && y <= f.bottom) return { ...a, frac: f.height > 0 ? (y - f.top) / f.height : 0 };
      const d = Math.min(Math.abs(y - f.top), Math.abs(y - f.bottom));
      if (d < bestD) { bestD = d; best = { ...a, frac: y < f.top ? 0 : 1 }; }
    }
    return best;
  }

  visibleBeats(): number[] {
    const s = this.structure;
    if (!s) return [];
    const h0 = this.el.clientHeight, out = new Set<number>();
    for (const [i, el] of this.paragraphEls) {
      const f = this.frame(el);
      if (f.bottom > 0 && f.top < h0) { const b = beatIndex(s, i); if (b !== null) out.add(b); }
    }
    return [...out].sort((a, b) => a - b);
  }

  get maxScroll() { return Math.max(0, this.el.scrollHeight - this.el.clientHeight); }

  private textNode(p: number): Text | null {
    const el = this.paragraphEls.get(p);
    return el?.firstChild instanceof Text ? el.firstChild : null;
  }

  private caretAt(x: number, y: number): { node: Node; offset: number } | null {
    const o = this.el.getBoundingClientRect();
    const cx = o.left + x, cy = o.top + y;
    const d = document as any;
    if (d.caretPositionFromPoint) { const c = d.caretPositionFromPoint(cx, cy); return c ? { node: c.offsetNode, offset: c.offset } : null; }
    if (d.caretRangeFromPoint) { const r = d.caretRangeFromPoint(cx, cy); return r ? { node: r.startContainer, offset: r.startOffset } : null; }
    return null;
  }

  /** The paragraph and character under a visible point (taps, long-press). */
  textPosition(x: number, y: number): { paragraph: number; offset: number } | null {
    const c = this.caretAt(x, y);
    const pEl = (c?.node instanceof Text ? c.node.parentElement : (c?.node as Element | undefined))?.closest?.("p.para") as HTMLElement | null;
    if (!c || !pEl) return null;
    return { paragraph: Number(pEl.dataset.p), offset: c.node instanceof Text ? c.offset : 0 };
  }

  /** The first character of the line at a visible y (the reading line, DEPTH.md §6). */
  lineStart(y: number): { paragraph: number; offset: number } | null {
    if (this.level < 0) return null;
    for (const [i, el] of this.paragraphEls) {
      const f = this.frame(el);
      if (y < f.top || y > f.bottom) continue;
      const node = this.textNode(i);
      if (!node) return { paragraph: i, offset: 0 };
      const c = this.caretAt(f.left + 1, Math.min(f.bottom - 1, Math.max(f.top + 1, y)));
      return { paragraph: i, offset: c && c.node === node ? this.lineFirstOffset(node, c.offset) : 0 };
    }
    return null;
  }

  /** Walks back from an offset to the first character on the same visual line. */
  private lineFirstOffset(node: Text, offset: number): number {
    const top = this.charTop(node, offset);
    if (top === null) return offset;
    let lo = 0, hi = offset;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      const t = this.charTop(node, mid);
      if (t !== null && t < top - 2) lo = mid + 1; else hi = mid;
    }
    return lo;
  }

  private charTop(node: Text, offset: number): number | null {
    const len = node.length;
    if (!len) return null;
    const o = Math.min(Math.max(0, offset), len - 1);
    const r = document.createRange();
    r.setStart(node, o); r.setEnd(node, o + 1);
    const rect = r.getClientRects()[0];
    return rect ? rect.top - this.el.getBoundingClientRect().top : null;
  }

  /** Top of the line holding character `offset` of a paragraph (visible y), or null. */
  lineTop(p: number, offset: number): number | null {
    const node = this.textNode(p);
    if (!node || offset >= node.length || offset < 0) return null;
    return this.charTop(node, offset);
  }

  rangeRect(p: number, start: number, end: number): DOMRect | null {
    const node = this.textNode(p);
    if (!node || end > node.length) return null;
    const r = document.createRange();
    r.setStart(node, start); r.setEnd(node, end);
    const b = r.getBoundingClientRect(), o = this.el.getBoundingClientRect();
    return new DOMRect(b.left - o.left, b.top - o.top, b.width, b.height);
  }

  // ---- narration (DEPTH.md §6): highlight sentence (soft) and word (strong) without reflowing

  showNarration(spot: { paragraph: number; word: [number, number] | null; sentence: [number, number] | null } | null) {
    const key = spot ? `${spot.paragraph}|${spot.word?.join(",")}|${spot.sentence?.join(",")}` : null;
    if (key === this.narrationKey) return;
    this.narrationKey = key;
    if (this.level >= 0) {
      const node = spot ? this.textNode(spot.paragraph) : null;
      const range = (a: number, b: number) => { const r = document.createRange(); r.setStart(node!, a); r.setEnd(node!, Math.min(b, node!.length)); return r; };
      const sentence = node && spot?.sentence ? range(...spot.sentence) : null;
      const word = node && spot?.word ? range(...spot.word) : null;
      if (supportsHighlights) {
        const hl = (CSS as any).highlights;
        hl.delete("narration-sentence"); hl.delete("narration-word");
        if (sentence) hl.set("narration-sentence", new (window as any).Highlight(sentence));
        if (word) hl.set("narration-word", new (window as any).Highlight(word));
      } else {
        this.overlay.textContent = "";
        const base = this.overlay.getBoundingClientRect();
        for (const [r, cls] of [[sentence, "s"], [word, "w"]] as const) {
          if (!r) continue;
          for (const rect of r.getClientRects()) {
            this.overlay.appendChild(h("i", { class: cls, style: { left: `${rect.left - base.left}px`, top: `${rect.top - base.top}px`, width: `${rect.width}px`, height: `${rect.height}px` } }));
          }
        }
      }
      for (const [i, el] of this.paragraphEls) el.classList.toggle("narrating", !!spot && !spot.word && i === spot.paragraph);
    } else {
      const k = spot ? anchorKeyFor(this.structure, spot.paragraph, this.level) : null;
      const el = k ? this.anchor(k) ?? null : null;
      if (el !== this.narratingAnchor) {
        this.narratingAnchor?.classList.remove("narrating");
        el?.classList.add("narrating");
        this.narratingAnchor = el;
      }
    }
  }

  clearNarration() {
    if (supportsHighlights && this.level >= 0) { (CSS as any).highlights.delete("narration-sentence"); (CSS as any).highlights.delete("narration-word"); }
    this.overlay.textContent = "";
    this.narrationKey = null;
  }

  /** Marks a word (long-press menu). */
  markWord(p: number | null, range?: [number, number]) {
    if (!supportsHighlights) return;
    const hl = (CSS as any).highlights;
    hl.delete("marked-word");
    const node = p === null ? null : this.textNode(p);
    if (node && range) { const r = document.createRange(); r.setStart(node, range[0]); r.setEnd(node, range[1]); hl.set("marked-word", new (window as any).Highlight(r)); }
  }
}
